import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const FHSouthAsian = () => {
  const [activeContentType, setActiveContentType] = useState<string | null>(null);

  const videoContent = [
    {
      title: 'South Asian Diet & Cholesterol Management',
      duration: '9:15',
      views: '21K views',
      thumbnail:
        'https://public.readdy.ai/ai/img_res/96cb69983ca6c29ff0c161cabbef2805.jpg',
    },
    {
      title: 'Genetic Testing in South Asian Communities',
      duration: '11:30',
      views: '16K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=South%20Asian%20doctor%20reviewing%20genetic%20test%20results%20with%20patient%20in%20modern%20medical%20clinic%20professional%20healthcare%20consultation%20warm%20lighting%20clean%20white%20office&width=400&height=250&seq=fhsa-vid-thumb2&orientation=landscape',
    },
    {
      title: 'Breaking the Silence on Heart Disease',
      duration: '14:05',
      views: '29K views',
      thumbnail:
        'https://readdy.ai/api/search-image?query=South%20Asian%20community%20health%20awareness%20event%20about%20cardiovascular%20disease%20prevention%20with%20medical%20professionals%20presenting%20to%20diverse%20audience%20warm%20lighting%20modern%20venue&width=400&height=250&seq=fhsa-vid-thumb3&orientation=landscape',
    },
  ];

  const handleContentTypeClick = (id: string) => {
    setActiveContentType(activeContentType === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              South Asian Cardiovascular Health Series
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Familial Hypercholesterolemia in<br />South Asian Population
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              South Asians face a disproportionately higher risk of cardiovascular disease. Learn how Familial Hypercholesterolemia uniquely impacts this population and why early screening is critical for families of South Asian descent.
            </p>
          </div>

          {/* Content Type Cards */}
          <div className="grid grid-cols-3 gap-6">
            {/* Blog Card */}
            <div
              onClick={() => handleContentTypeClick('blog')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'blog'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=South%20Asian%20elderly%20man%20reading%20medical%20health%20report%20about%20cholesterol%20with%20concerned%20family%20members%20around%20him%20in%20warm%20living%20room%20setting%20traditional%20Indian%20home%20decor%20natural%20lighting%20professional%20photography&width=400&height=300&seq=fhsa-blog-card-001&orientation=landscape"
                  alt="Blog articles about FH in South Asian population"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'blog' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-article-line text-xl transition-colors ${
                      activeContentType === 'blog' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Blog</h3>
                <p className="text-gray-500 text-sm">In-depth articles about FH in South Asians</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'blog' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'blog' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Video Card */}
            <div
              onClick={() => handleContentTypeClick('videos')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'videos'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=South%20Asian%20cardiologist%20in%20white%20coat%20presenting%20cardiovascular%20health%20data%20on%20screen%20in%20modern%20hospital%20conference%20room%20with%20medical%20charts%20about%20cholesterol%20levels%20professional%20medical%20photography%20warm%20lighting&width=400&height=300&seq=fhsa-video-card-001&orientation=landscape"
                  alt="Educational videos about FH in South Asians"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'videos' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-video-line text-xl transition-colors ${
                      activeContentType === 'videos' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Video</h3>
                <p className="text-gray-500 text-sm">Educational video content</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'videos' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'videos' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>

            {/* Infograph Card */}
            <div
              onClick={() => handleContentTypeClick('infograph')}
              className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg group ${
                activeContentType === 'infograph'
                  ? 'ring-2 ring-[#f04e58] shadow-lg'
                  : 'shadow-sm hover:shadow-md'
              }`}
            >
              <div className="w-full h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=colorful%20medical%20infographic%20about%20cardiovascular%20disease%20risk%20factors%20in%20South%20Asian%20population%20displayed%20on%20large%20monitor%20showing%20heart%20health%20statistics%20charts%20and%20demographic%20data%20modern%20data%20visualization%20coral%20and%20teal%20color%20accents&width=400&height=300&seq=fhsa-infograph-card-001&orientation=landscape"
                  alt="Infographics about FH in South Asians"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58]' : 'bg-[#fef5f5] group-hover:bg-[#f04e58]'
                  }`}
                >
                  <i
                    className={`ri-bar-chart-box-line text-xl transition-colors ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-[#f04e58] group-hover:text-white'
                    }`}
                  ></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Infograph</h3>
                <p className="text-gray-500 text-sm">Visual guides and data</p>
                <div
                  className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    activeContentType === 'infograph' ? 'bg-[#f04e58] rotate-45' : 'bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg ${
                      activeContentType === 'infograph' ? 'text-white' : 'text-gray-700'
                    }`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      {activeContentType === 'blog' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Article Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                The Hidden Genetic Heart Risk Millions of South Asians Don't Know They Have
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  Jan 15, 2025
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  10 min read
                </span>
              </div>
              <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=multigenerational%20South%20Asian%20Indian%20family%20portrait%20with%20grandparents%20parents%20and%20children%20in%20warm%20home%20setting%20discussing%20health%20and%20wellness%20together%20natural%20lighting%20professional%20photography%20soft%20warm%20tones&width=800&height=400&seq=fhsa-blog-hero-001&orientation=landscape"
                  alt="South Asian family and heart health"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Introduction: The Story We Think We Know
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                  For years, the conversation about heart disease in the South Asian community has focused on diet and lifestyle. But what if the story we know is incomplete? For millions, the greatest threat isn't just about what they eat; it's a hidden genetic condition they were born with, one that is frequently missed or misdiagnosed until it's too late.
                </p>
              </div>

              {/* Main Section Header */}
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Four Surprising Truths About a Hidden Genetic Risk
                </h3>
              </div>

              {/* Section 1 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    It's Not Just Your Diet, It's In Your DNA
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Familial Hypercholesterolemia (FH) is not a condition you develop—it's one you inherit. It is a genetic disorder that causes the body to have dangerously high levels of LDL ("bad") cholesterol from the moment of birth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This is a critical distinction. Unlike high cholesterol that develops later in life due to lifestyle factors, FH is a lifelong condition coded into your DNA. This means that while a healthy lifestyle is crucial for everyone, it cannot overcome the genetic driver of FH.
                </p>
                <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
                  <p className="text-gray-900 font-semibold mb-2">Critical Understanding:</p>
                  <p className="text-gray-700 leading-relaxed">
                    For those with this condition, poor lifestyle choices can dangerously amplify the already high genetic risk, making early medical intervention essential.
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Scale of the Problem is Staggering—and Mostly Invisible
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  An estimated <strong>7 to 8 million people in South Asia have Familial Hypercholesterolemia right now</strong>, and that number is projected to climb to around 9 million by 2030. Yet, the vast majority of these individuals have never been diagnosed.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The lack of awareness is the most critical part of the problem, a fact underscored by one staggering statistic:
                </p>
                <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6 text-white mb-4">
                  <p className="text-xl font-bold text-white text-center leading-relaxed">
                    "Roughly 1 in every 300 South Asians may have FH — most don't know it."
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  This low detection rate, combined with population growth, is a major reason why this silent condition continues to spread, undiagnosed, through families.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    The Risk is Extreme, But So is the Opportunity for Prevention
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The danger of untreated FH is severe. Individuals with the condition face a <strong>20-times higher risk of developing early heart disease</strong>, which can lead to heart attacks as early as their 30s or 40s.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="w-24 h-24 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-3">
                        <span className="text-3xl font-bold text-[#f04e58]">20x</span>
                      </div>
                      <p className="text-sm text-gray-600 font-medium">Higher Risk of Early Heart Disease</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  However, the outlook changes completely with early detection and treatment. When FH is identified and managed with cholesterol-lowering therapies, the risk of heart disease can be reduced to nearly that of the general population.
                </p>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-6 text-white">
                  <p className="font-semibold mb-2 text-white">Powerful Truth:</p>
                  <p className="text-white/95 leading-relaxed">
                    Knowledge and proactive treatment can completely alter a person's health trajectory, turning a dangerous genetic predisposition into a manageable condition.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div className="mb-12 bg-[#fef9f9] rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">
                    A Diagnosis is a Wake-Up Call for Your Entire Family
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Because FH is genetic, a diagnosis for one person has immediate implications for their entire family. If a parent has FH, <strong>each of their children has a 50% chance of inheriting the same condition</strong>.
                </p>
                <div className="bg-white rounded-xl p-6 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    You should ask your doctor for a cholesterol test if you have any of the following risk factors:
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700 leading-relaxed">
                        A family member had a heart attack before age 50 (for men) or 60 (for women).
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700 leading-relaxed">
                        You have a personal history of LDL cholesterol over 190 mg/dL.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700 leading-relaxed">
                        You have visible signs like xanthomas (yellowish bumps of cholesterol deposits near the eyes or on tendons).
                      </span>
                    </li>
                  </ul>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Given the 50% inheritance rate, family screening is one of the most effective tools for finding and treating FH before it can cause irreversible damage.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-12 bg-gradient-to-br from-[#fef9f9] to-[#faf5f8] rounded-2xl p-8 border-2 border-[#f04e58]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Beyond History, Toward a Healthier Future
                </h3>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  For generations, the story of heart disease in South Asian families has been whispered as a tale of fate or diet. Now, we know it is often a story written in our DNA—one that we have the power to read and rewrite.
                </p>
                <p className="text-gray-900 font-semibold text-lg">
                  Have you asked your parents and siblings about their cholesterol levels and heart health history?
                </p>
              </div>

              {/* Call to Action */}
              <div className="mt-12 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Take the First Step for Your Family</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  If you're of South Asian descent and have a family history of high cholesterol or early heart disease, early screening could save your life—and the lives of your loved ones.
                </p>
                <Link
                  to="/consultation"
                  className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Screening
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </article>
          </div>
        </section>
      )}

      {/* Video Content Section */}
      {activeContentType === 'videos' && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Video Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                FH in South Asians: What Every Family Should Know
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Watch this educational video to understand why South Asians face unique cardiovascular risks and how Familial Hypercholesterolemia impacts this population disproportionately.
              </p>
            </div>

            {/* Video Player */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl bg-black aspect-video mb-8">
              <video
                controls
                autoPlay
                className="w-full h-full"
                poster="https://readdy.ai/api/search-image?query=South%20Asian%20cardiologist%20presenting%20cardiovascular%20health%20data%20about%20familial%20hypercholesterolemia%20to%20diverse%20audience%20in%20modern%20medical%20conference%20room%20professional%20healthcare%20presentation%20warm%20lighting&width=800&height=450&seq=fhsa-video-poster-main&orientation=landscape"
              >
                <source
                  src="https://lh3.googleusercontent.com/notebooklm/ANHLwAwe_ZHWGTJS2P7FfI8_H9gmnpsd72GupNmibNCvcbEqnGXFQmZO3C_rrXtNSweN19UjlnxBETXE69qtpFgxWETMzyeomQMbIaHgjBbLFst74LaS2O0ObsRIUuGO6Jdbh6tfJ7xpzxnUOQ83G9TvPOcdKxS-LQw=m22?authuser=0"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Info */}
            <div className="bg-[#fef9f9] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#f04e58] flex items-center justify-center">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">South Asian Heart Health Series</h3>
                  <p className="text-sm text-gray-500">FH in South Asian Population</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                This video covers the unique cardiovascular risk profile of South Asians, the role of Familial Hypercholesterolemia, cultural dietary considerations, and actionable steps for families to protect their heart health through early screening and prevention.
              </p>
            </div>

            {/* Related Videos */}
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Educational Videos</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {videoContent.map((video, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <i className="ri-play-fill text-[#f04e58] text-2xl"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-[#f04e58] transition-colors">
                        {video.title}
                      </h4>
                      <p className="text-xs text-gray-500">{video.views}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Infograph Content Section */}
      {activeContentType === 'infograph' && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            {/* Infograph Header */}
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Hidden Heart Threat: Understanding FH in South Asia
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
                A silent genetic epidemic with millions at risk, preventable with early action.
              </p>
            </div>

            {/* Main Infographic */}
            <div className="bg-[#f0f4f8] rounded-2xl p-4 shadow-lg mb-4">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/09e44e69c20da4e465975677dd995976.png"
                  alt="The Hidden Heart Threat: Understanding FH in South Asia - Infographic showing statistics for Pakistan, India, Bangladesh with growing crisis data and detection guidelines"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Second Infographic */}
            <div className="bg-[#f0f4f8] rounded-2xl p-4 shadow-lg mb-12">
              <div className="w-full rounded-xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/ff0b7e80ce034da2f0ffbed7fce4b50d/faabaeb640500245b6f56fd7e9e6f1ca.png"
                  alt="Silent Heart Threat: Familial Hypercholesterolemia (FH) in South Asia - detailed infographic with genetic burden statistics, identification guidelines, and treatment information"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>

            {/* Key Highlights */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-global-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Elevated Risk Profile</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  South Asians have 3–4x higher cardiovascular risk, with heart attacks occurring 5–10 years earlier than other populations.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 mb-4">
                  <i className="ri-search-eye-line text-xl text-[#0d9488]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Massive Diagnosis Gap</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Fewer than 1% of FH cases are diagnosed in South Asian countries, leaving millions at risk without knowing it.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 mb-4">
                  <i className="ri-team-line text-xl text-[#f04e58]"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Family Cascade Screening</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Strong South Asian family networks make cascade screening highly effective—one diagnosis can protect an entire extended family.
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button className="inline-flex items-center px-6 py-3 bg-[#f04e58] text-white rounded-full font-medium hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-download-line mr-2"></i>
                Download Infographic
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-share-line mr-2"></i>
                Share
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default FHSouthAsian;
